/**
 * ProductForm — create/edit dialog contents for products (Story 7.4 UI).
 *
 * Fields:
 *   - externalId (create only, locked on edit — it's the catalog reconciliation key)
 *   - name (required)
 *   - tier (select: basic | premium)
 *   - price (number, BRL, 2 decimals)
 *   - paymentLink (required URL)
 *   - discountLink (optional URL)
 *   - discountPercent (optional integer 0-100)
 *   - active (toggle, edit only — create defaults to active=true server-side)
 *
 * Client-side validation:
 *   - Non-empty name
 *   - price >= 0
 *   - URL format for payment/discount links (native URL constructor)
 *   - discountPercent between 0-100
 *
 * The form is uncontrolled-ish: parent owns initial, component owns intermediate
 * state and calls onSubmit with the final payload when valid. Server returns
 * 409 EXTERNAL_ID_TAKEN on duplicate externalId — parent surfaces as toast.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type ProductTier = 'basic' | 'premium';

export interface ProductFormValue {
  externalId: string;
  name: string;
  description: string | null;
  tier: ProductTier;
  price: number;
  paymentLink: string | null;
  discountLink: string | null;
  /** Link de acesso à plataforma (produto já comprado). Opcional. */
  accessLink: string | null;
  /**
   * 2026-08-04 (Story 9.11c follow-up) — link de vídeo específico (ex.:
   * recado de boas-vindas), usado no texto-guia de botões AI_RESPONSE via
   * {{special_video_link}}. Opcional.
   */
  specialVideoLink: string | null;
  discountPercent: number | null;
  active: boolean;
}

export const DEFAULT_PRODUCT_FORM: ProductFormValue = {
  externalId: '',
  name: '',
  description: null,
  tier: 'basic',
  price: 0,
  paymentLink: '',
  discountLink: null,
  accessLink: null,
  specialVideoLink: null,
  discountPercent: null,
  active: true,
};

const TIER_OPTIONS: ProductTier[] = ['basic', 'premium'];

/** Validate a URL string — empty is acceptable only for optional fields. */
export function isValidUrl(value: string): boolean {
  if (!value) return false;
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

interface Props {
  mode: 'create' | 'edit';
  initial?: ProductFormValue;
  submitting?: boolean;
  onSubmit: (next: ProductFormValue) => void;
  onCancel: () => void;
}

export function ProductForm({ mode, initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<ProductFormValue>(initial ?? DEFAULT_PRODUCT_FORM);
  const [paymentLinkError, setPaymentLinkError] = useState<string | null>(null);
  const [discountLinkError, setDiscountLinkError] = useState<string | null>(null);
  const [accessLinkError, setAccessLinkError] = useState<string | null>(null);
  const [specialVideoLinkError, setSpecialVideoLinkError] = useState<string | null>(null);
  const [discountPercentError, setDiscountPercentError] = useState<string | null>(null);

  // Sync when the `initial` prop changes (edit target switches).
  useEffect(() => {
    if (initial) setForm(initial);
  }, [initial]);

  function patch<K extends keyof ProductFormValue>(key: K, value: ProductFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validatePaymentLink(next: string): string | null {
    if (!next) return 'Payment link é obrigatório.';
    if (!isValidUrl(next)) return 'URL inválida (use http:// ou https://).';
    return null;
  }

  function validateDiscountLink(next: string | null): string | null {
    if (!next) return null;
    if (!isValidUrl(next)) return 'URL inválida (use http:// ou https://).';
    return null;
  }

  function validateAccessLink(next: string | null): string | null {
    if (!next) return null;
    if (!isValidUrl(next)) return 'URL inválida (use http:// ou https://).';
    return null;
  }

  function validateSpecialVideoLink(next: string | null): string | null {
    if (!next) return null;
    if (!isValidUrl(next)) return 'URL inválida (use http:// ou https://).';
    return null;
  }

  function validateDiscountPercent(next: number | null): string | null {
    if (next === null) return null;
    if (!Number.isInteger(next) || next < 0 || next > 100) {
      return 'Desconto deve ser um inteiro entre 0 e 100.';
    }
    return null;
  }

  function handlePaymentLinkChange(next: string) {
    patch('paymentLink', next);
    setPaymentLinkError(validatePaymentLink(next));
  }

  function handleDiscountLinkChange(next: string) {
    const val = next === '' ? null : next;
    patch('discountLink', val);
    setDiscountLinkError(validateDiscountLink(val));
  }

  function handleAccessLinkChange(next: string) {
    const val = next === '' ? null : next;
    patch('accessLink', val);
    setAccessLinkError(validateAccessLink(val));
  }

  function handleSpecialVideoLinkChange(next: string) {
    const val = next === '' ? null : next;
    patch('specialVideoLink', val);
    setSpecialVideoLinkError(validateSpecialVideoLink(val));
  }

  function handleDiscountPercentChange(next: string) {
    const val = next === '' ? null : Number(next);
    patch('discountPercent', val);
    setDiscountPercentError(validateDiscountPercent(val));
  }

  function canSubmit(): boolean {
    if (!form.name.trim()) return false;
    if (mode === 'create' && !form.externalId.trim()) return false;
    if (form.price < 0 || Number.isNaN(form.price)) return false;
    if (paymentLinkError) return false;
    if (discountLinkError) return false;
    if (accessLinkError) return false;
    if (specialVideoLinkError) return false;
    if (discountPercentError) return false;
    if (!form.paymentLink || !isValidUrl(form.paymentLink)) return false;
    return true;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Re-validate on submit in case user never touched the fields.
    const pErr = validatePaymentLink(form.paymentLink ?? '');
    const dErr = validateDiscountLink(form.discountLink);
    const aErr = validateAccessLink(form.accessLink);
    const svErr = validateSpecialVideoLink(form.specialVideoLink);
    const pctErr = validateDiscountPercent(form.discountPercent);
    setPaymentLinkError(pErr);
    setDiscountLinkError(dErr);
    setAccessLinkError(aErr);
    setSpecialVideoLinkError(svErr);
    setDiscountPercentError(pctErr);
    if (pErr || dErr || aErr || svErr || pctErr) return;
    if (!canSubmit()) return;
    onSubmit(form);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-1">
        <Label htmlFor="product-external-id">External ID</Label>
        <Input
          id="product-external-id"
          value={form.externalId}
          disabled={mode === 'edit' || submitting}
          onChange={(e) => patch('externalId', e.target.value)}
          placeholder="SKU-001"
          required
          aria-invalid={!form.externalId.trim() && mode === 'create' ? 'true' : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Chave de reconciliação com o catálogo externo (não pode ser alterada após criação).
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="product-name">Nome</Label>
        <Input
          id="product-name"
          value={form.name}
          disabled={submitting}
          onChange={(e) => patch('name', e.target.value)}
          placeholder="Ex.: Curso Avançado de Fotografia"
          required
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="product-description">Descrição</Label>
        <textarea
          id="product-description"
          className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={form.description ?? ''}
          disabled={submitting}
          onChange={(e) => patch('description', e.target.value || null)}
          placeholder="Descrição breve do produto…"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="product-tier">Tier</Label>
          <select
            id="product-tier"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={form.tier}
            disabled={submitting}
            onChange={(e) => patch('tier', e.target.value as ProductTier)}
          >
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="product-price">Preço (R$)</Label>
          <Input
            id="product-price"
            type="number"
            min={0}
            step={0.01}
            value={form.price}
            disabled={submitting}
            onChange={(e) => patch('price', Number(e.target.value))}
            required
          />
          <p className="text-xs text-muted-foreground">Em reais (BRL). Use duas casas decimais.</p>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="product-payment-link">Payment link</Label>
        <Input
          id="product-payment-link"
          type="url"
          value={form.paymentLink ?? ''}
          disabled={submitting}
          onChange={(e) => handlePaymentLinkChange(e.target.value)}
          placeholder="https://pay.example.com/produto"
          required
          aria-invalid={paymentLinkError ? 'true' : undefined}
        />
        {paymentLinkError ? (
          <p role="alert" className="text-xs text-destructive">
            {paymentLinkError}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="product-discount-link">Discount link</Label>
          <Input
            id="product-discount-link"
            type="url"
            value={form.discountLink ?? ''}
            disabled={submitting}
            onChange={(e) => handleDiscountLinkChange(e.target.value)}
            placeholder="https://pay.example.com/produto?disc=10"
            aria-invalid={discountLinkError ? 'true' : undefined}
          />
          {discountLinkError ? (
            <p role="alert" className="text-xs text-destructive">
              {discountLinkError}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="product-discount-percent">Desconto (%)</Label>
          <Input
            id="product-discount-percent"
            type="number"
            min={0}
            max={100}
            step={1}
            value={form.discountPercent ?? ''}
            disabled={submitting}
            onChange={(e) => handleDiscountPercentChange(e.target.value)}
            placeholder="10"
            aria-invalid={discountPercentError ? 'true' : undefined}
          />
          {discountPercentError ? (
            <p role="alert" className="text-xs text-destructive">
              {discountPercentError}
            </p>
          ) : null}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="product-access-link">Link de acesso</Label>
        <Input
          id="product-access-link"
          type="url"
          value={form.accessLink ?? ''}
          disabled={submitting}
          onChange={(e) => handleAccessLinkChange(e.target.value)}
          placeholder="https://app.example.com/login"
          aria-invalid={accessLinkError ? 'true' : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Link da plataforma/área do aluno pra quem já comprou. A Rafa usa quando o lead pergunta
          "onde acesso?". Opcional.
        </p>
        {accessLinkError ? (
          <p role="alert" className="text-xs text-destructive">
            {accessLinkError}
          </p>
        ) : null}
      </div>

      <div className="space-y-1">
        <Label htmlFor="product-special-video-link">Link de vídeo especial</Label>
        <Input
          id="product-special-video-link"
          type="url"
          value={form.specialVideoLink ?? ''}
          disabled={submitting}
          onChange={(e) => handleSpecialVideoLinkChange(e.target.value)}
          placeholder="https://youtube.com/..."
          aria-invalid={specialVideoLinkError ? 'true' : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Vídeo específico desse produto (ex.: recado de boas-vindas), usável no texto-guia de
          botões "Resposta da IA" via <code>{'{{special_video_link}}'}</code>. Opcional.
        </p>
        {specialVideoLinkError ? (
          <p role="alert" className="text-xs text-destructive">
            {specialVideoLinkError}
          </p>
        ) : null}
      </div>

      {mode === 'edit' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.active}
            disabled={submitting}
            onChange={(e) => patch('active', e.target.checked)}
            aria-label="Produto ativo"
          />
          Produto ativo
        </label>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting || !canSubmit()}>
          {submitting ? 'Salvando…' : mode === 'create' ? 'Criar produto' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}

export default ProductForm;
